// schema
import * as ISchema from "@schema";

// easy-to-reference constants
export const standardGoals = {
  Lunch: "Lunch",
  EmailTriage: "EmailTriage",
  FocusTime: "FocusTime",
  PreferredWork: "PreferredWork",
  MeetingRelief: "MeetingRelief",
  TravelTime: "TravelTime",
};

function getAggregateSum(teamGoalScores: ISchema.ITeamGoalScores[], goalIds: string[]) {
  let anyGoalsFound = false;
  const aggregate = goalIds.reduce((sum, goalId) => {
    const goals = getAggregateGoalScores(teamGoalScores, goalId);
    let newSum = sum;
    if (goals) {
      anyGoalsFound = true;
      goals.forEach((goal) => {
        newSum += goal.after - goal.before;
      });
    }
    return newSum;
  }, 0);

  return anyGoalsFound ? aggregate : undefined;
}

export function getAggregateSumHours(teamGoalScores: ISchema.ITeamGoalScores[]) {
  return getAggregateSum(teamGoalScores, [standardGoals.FocusTime, standardGoals.PreferredWork]);
}

export function getAggregateSumGoalScores(teamGoalScores: ISchema.ITeamGoalScores[]) {
  return getAggregateSum(teamGoalScores, [standardGoals.EmailTriage, standardGoals.Lunch]);
}

export function getAggregateSingleScore(teamGoalScores: ISchema.ITeamGoalScores[], goalId: string) {
  const goals = getAggregateGoalScores(teamGoalScores, goalId);
  let sum = 0;
  if (goals && goals.length) {
    goals.forEach((goal) => {
      sum += goal.after - goal.before;
    });
    return sum;
  }
  return 0;
}

export function getAggregateGoalScores(teamGoalScores: ISchema.ITeamGoalScores[], goalId: string) {
  let scores: ISchema.IGoalScore[] = [];
  teamGoalScores.forEach((tgs) => {
    scores = scores.concat(tgs.aggregatedGoalScores);
  });

  if (!scores || !scores.length) {
    return;
  }

  return scores.filter((score) => score.goalId === goalId);
}

export function getSortedCalendarGoalScores(
  teamGoalScores: ISchema.ITeamGoalScores[],
  userEmails: string[],
  goalId: string,
) {
  let scores: ISchema.ICalendarGoalScores[] = [];
  if (!teamGoalScores.length) {
    return scores;
  }

  scores = scores.concat(teamGoalScores[0].calendarGoalScores);
  for (let i = 1; i < teamGoalScores.length; i++) {
    // iterate through team goal scores and collapse calendar goal scores to a single entry per calendarId
    teamGoalScores[i].calendarGoalScores.forEach((calendarGoalScore) => {
      const foundScore = scores.find((cgs) => cgs.calendarId === calendarGoalScore.calendarId);
      if (foundScore) {
        const addGoal = calendarGoalScore.goalScores.find((score) => score.goalId === goalId);
        const foundGoal = foundScore.goalScores.find((score) => score.goalId === goalId);
        if (foundGoal && addGoal) {
          foundGoal.after += addGoal.after;
          foundGoal.before += addGoal.before;
        }
      } else {
        scores.push(calendarGoalScore);
      }
    });
  }

  scores.sort((a, b) => {
    const aGoal = a.goalScores.find((score) => score.goalId === goalId);
    const bGoal = b.goalScores.find((score) => score.goalId === goalId);
    if (!aGoal && !bGoal) {
      return a.calendarId.localeCompare(b.calendarId);
    } else if (aGoal && !bGoal) {
      return -1;
    } else if (bGoal && !aGoal) {
      return 1;
    } else if (aGoal && bGoal) {
      // always push you to the top
      if (userEmails.indexOf(a.calendarId) > -1) {
        return -1;
      } else if (userEmails.indexOf(b.calendarId) > -1) {
        return 1;
      }
      return bGoal.after - bGoal.before - (aGoal.after - aGoal.before);
    }
    return 0; // not possible, but placating the static analysis
  });

  return scores;
}

export function findGoal(userGoals: ISchema.IGoalConnection, goalId: string): ISchema.IGoal | null {
  const goals = userGoals.edges.map((edge) => edge.node);
  return goals.find((goal) => !goal.isDeleted && goal.goalId === goalId) || null;
}

export function findGoalByName(
  userGoals: ISchema.IGoalConnection,
  name: keyof typeof standardGoals,
): ISchema.IGoal | null {
  const goals = userGoals.edges.map((edge) => edge.node);
  return goals.find((goal) => !goal.isDeleted && goal.name === name) || null;
}
